import React, { useState, useEffect, useRef } from "react";
import {
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp,
  Eye,
  CreditCard,
  SendHorizonal,
  FileText,
} from "lucide-react";
import FeeBillsModal from "../../modals/admin/FeeBillsModal";
import CustomDropdown from "../../Components/admin/CustomDropdown";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUnpaidOrOverdueBills,
  makePayment,
} from "../../redux/feeStructureSlice";
import {
  sendNotification,
  clearNotificationStatus,
} from "../../redux/notificationsSlice";
import NotificationsModal from "../../Components/Common/NotificationModal";
import toast, { Toaster } from "react-hot-toast";
import {
  formatDate,
  generateInvoicePDF,
  defaultSchoolInfo,
} from "../../utils/invoiceUtils";

function FeeBills({ feeBills, setFeeBills }) {
  const dispatch = useDispatch();
  const {
    unpaidOrOverdueBills = [],
    loading,
    paymentLoading,
  } = useSelector((state) => state.feeStructures);
  const notificationStatus = useSelector(
    (state) => state.notifications?.sendStatus
  );
  const notificationError = useSelector((state) => state.notifications?.error);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isNotificationsModal, setIsNotificationsModal] = useState(false);

  const [editingData, setEditingData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [expandedRowIndex, setExpandedRowIndex] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [filters, setFilters] = useState({
    studentName: "",
    enrollmentNo: "",
    status: "",
    feeCode: "",
    campus: "",
  });

  // Pagination state
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Get unique campus list for filtering
  const campusList = React.useMemo(() => {
    if (!Array.isArray(unpaidOrOverdueBills)) return [];
    
    const campuses = unpaidOrOverdueBills
      .filter(bill => bill.campus?.name)
      .map(bill => bill.campus.name);
    
    return [...new Set(campuses)];
  }, [unpaidOrOverdueBills]);

  useEffect(() => {
    dispatch(fetchUnpaidOrOverdueBills());
  }, [dispatch]);

  useEffect(() => {
    if (notificationStatus === "succeeded") {
      toast.success("Notification sent successfully!");
      dispatch(clearNotificationStatus());
      setIsNotificationsModal(false);
      setSelectedStudents([]);
    } else if (notificationStatus === "failed" && notificationError) {
      toast.error(`Failed to send notification: ${notificationError}`);
      dispatch(clearNotificationStatus());
    }
  }, [notificationStatus, notificationError, dispatch]);

  // Reset page number when filters change
  useEffect(() => {
    setPageNumber(1);
  }, [filters]);

  const toggleView = (index) => {
    setExpandedRowIndex(expandedRowIndex === index ? null : index);
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const openPaymentModal = (bill) => {
    setPaymentData({
      studentId: bill.studentId,
      studentName: bill.fullName,
      enrollmentNo: bill.enrollmentNo,
      pendingAmount: bill.pendingAmount,
      billId: bill.id || "BILL-" + Math.floor(Math.random() * 1000),
      dueDate: bill.dueDate,
      campus: bill.campus,
    });
    setIsPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    dispatch(fetchUnpaidOrOverdueBills());
    setIsPaymentModalOpen(false);
  };

  const toggleStudentSelection = (studentId, studentName) => {
    setSelectedStudents((prev) => {
      const isSelected = prev.some((student) => student.id === studentId);
      if (isSelected) {
        return prev.filter((student) => student.id !== studentId);
      } else {
        return [...prev, { id: studentId, name: studentName }];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedStudents.length === paginatedFeeBills.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(
        paginatedFeeBills.map((bill) => ({
          id: bill.studentId,
          name: bill.fullName,
        }))
      );
    }
  };

  const handleSubmitNotification = (payload) => {
    const requestData = {
      UserIds: payload.recipients.map((recipient) => recipient.id),
      Title: payload.title,
      Body: payload.message,
    };
    dispatch(sendNotification(requestData));
  };

  // Handle invoice generation with the updated utility function
  const handleGenerateInvoice = (bill) => {
    generateInvoicePDF(bill, defaultSchoolInfo);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage !== pageNumber) {
      setPageNumber(newPage);
    }
  };

  // Filtered fee bills with campus filter
  const filteredFeeBills = Array.isArray(unpaidOrOverdueBills)
    ? unpaidOrOverdueBills.filter(
        (bill) =>
          (filters.studentName === "" ||
            (bill.fullName &&
              bill.fullName
                .toLowerCase()
                .includes(filters.studentName.toLowerCase()))) &&
          (filters.enrollmentNo === "" ||
            (bill.enrollmentNo &&
              bill.enrollmentNo
                .toLowerCase()
                .includes(filters.enrollmentNo.toLowerCase()))) &&
          (filters.status === "" ||
            (bill.status && bill.status.includes(filters.status))) &&
          (filters.feeCode === "" ||
            (bill.feeCode && bill.feeCode.includes(filters.feeCode))) &&
          (filters.campus === "" ||
            (bill.campus?.name && 
              bill.campus.name === filters.campus))
      )
    : [];

  // Calculate total pages and paginated data
  const totalPages = Math.ceil(filteredFeeBills.length / pageSize);
  const startIndex = (pageNumber - 1) * pageSize;
  const paginatedFeeBills = filteredFeeBills.slice(startIndex, startIndex + pageSize);

  return (
    <div>
      <div className="mb-4 flex gap-4 w-full flex-wrap">
        <div className="flex w-3/6 md:w-1/4">
          <input
            type="text"
            placeholder="Search Student Name..."
            className="border border-gray-300 rounded p-2 w-full"
            value={filters.studentName}
            onChange={(e) => handleFilterChange("studentName", e.target.value)}
          />
        </div>
        <div className="flex w-3/6 md:w-1/4">
          <input
            type="text"
            placeholder="Search Enrollment No..."
            className="border border-gray-300 rounded p-2 w-full"
            value={filters.enrollmentNo}
            onChange={(e) => handleFilterChange("enrollmentNo", e.target.value)}
          />
        </div>
        <div className="w-1/2 md:w-1/6">
          <CustomDropdown
            options={["Pending", "Overdue"]}
            value={filters.status}
            onChange={(value) => handleFilterChange("status", value)}
            placeholder="Select Status"
            className="w-full"
          />
        </div>
        <div className="w-1/2 md:w-1/6">
          <CustomDropdown
            options={["1001", "1002"]}
            value={filters.feeCode}
            onChange={(value) => handleFilterChange("feeCode", value)}
            placeholder="Select FeeCode"
            className="w-full"
          />
        </div>
        {campusList.length > 0 && (
          <div className="w-1/2 md:w-1/6">
            <CustomDropdown
              options={campusList}
              value={filters.campus}
              onChange={(value) => handleFilterChange("campus", value)}
              placeholder="Select Campus"
              className="w-full"
            />
          </div>
        )}
        <div
          className="bg-[#241763] p-2 rounded cursor-pointer"
          onClick={() => setIsNotificationsModal(true)}
          title="Send Notification"
        >
          <SendHorizonal className="text-white w-6 h-6" />
        </div>
      </div>

      <div className="overflow-x-auto mt-6">
        {loading ? (
          <div className="text-center p-4">Loading...</div>
        ) : (
          <table className="table-auto w-full mt-6">
            <thead className="py-4 border-b font-bold">
              <tr className="bg-gray-100 border-b">
                <th>
                  <input
                    type="checkbox"
                    checked={
                      paginatedFeeBills.length > 0 &&
                      selectedStudents.length === paginatedFeeBills.length
                    }
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Student Name</th>
                <th>Enrollment No</th>
                <th>Campus</th>
                <th>Billing Cycle Start</th>
                <th>Billing Cycle End</th>
                <th>Due Date</th>
                <th>Total Fee</th>
                <th>Discount</th>
                <th>Final Amount</th>
                <th>Pending Amount</th>
                <th>Status</th>
                <th>Invoice</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {paginatedFeeBills.length > 0 ? (
                paginatedFeeBills.map((bill, index) => (
                  <React.Fragment key={index}>
                    <tr className="hover:bg-gray-200">
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedStudents.some(
                            (s) => s.id === bill.studentId
                          )}
                          onChange={() =>
                            toggleStudentSelection(
                              bill.studentId,
                              bill.fullName
                            )
                          }
                        />
                      </td>
                      <td>{bill.fullName}</td>
                      <td>{bill.enrollmentNo}</td>
                      <td>{bill.campus?.name || "-"}</td>
                      <td>{formatDate(bill.billingCycleStartDate)}</td>
                      <td>{formatDate(bill.billingCycleEndDate)}</td>
                      <td>{formatDate(bill.dueDate)}</td>
                      <td>${bill.totalFee?.toFixed(2)}</td>
                      <td>${bill.discountAmount?.toFixed(2)}</td>
                      <td>${bill.finalAmount?.toFixed(2)}</td>
                      <td>${bill.pendingAmount?.toFixed(2)}</td>
                      <td>
                        <span
                          className={
                            bill.status === "Overdue"
                              ? "text-red-600 font-medium"
                              : "text-orange-500 font-medium"
                          }
                        >
                          {bill.status}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="flex justify-center">
                          <span
                            onClick={() => handleGenerateInvoice(bill)}
                            className="text-[#241763] cursor-pointer flex items-center"
                            title="View Invoice"
                          >
                            <FileText className="w-5 h-5" />
                          </span>
                        </div>
                      </td>

                      <td className="space-x-2 flex items-center py-2">
                        <button
                          onClick={() => openPaymentModal(bill)}
                          className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                          title="Receive Payment"
                        >
                          <CreditCard className="text-[#241763] w-4 h-4 group-hover:text-white" />
                        </button>
                        <div
                          onClick={() => toggleView(index)}
                          className="cursor-pointer bg-[#B6A5C9]/50 p-2 rounded hover:bg-[#241763]/60 group"
                        >
                          {expandedRowIndex === index ? (
                            <ChevronUp
                              size={16}
                              className="group-hover:text-white"
                            />
                          ) : (
                            <ChevronDown
                              size={16}
                              className="group-hover:text-white"
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedRowIndex === index && (
                      <tr>
                        <td colSpan="14">
                          <div className="bg-gray-100 p-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p>
                                  <strong>Student ID:</strong> {bill.studentId}
                                </p>
                                <p>
                                  <strong>Student Name:</strong> {bill.fullName}
                                </p>
                                <p>
                                  <strong>Enrollment No:</strong>{" "}
                                  {bill.enrollmentNo}
                                </p>
                                <p>
                                  <strong>Status:</strong> {bill.status}
                                </p>
                                <p>
                                  <strong>Due Date:</strong>{" "}
                                  {formatDate(bill.dueDate)}
                                </p>
                                {bill.campus && (
                                  <>
                                    <p>
                                      <strong>Campus:</strong> {bill.campus.name}
                                    </p>
                                    <p>
                                      <strong>Campus Location:</strong> {bill.campus.location}
                                    </p>
                                  </>
                                )}
                              </div>
                              <div>
                                <p>
                                  <strong>Total Fee:</strong> $
                                  {bill.totalFee?.toFixed(2)}
                                </p>
                                <p>
                                  <strong>Discount:</strong> $
                                  {bill.discountAmount?.toFixed(2)}
                                </p>
                                <p>
                                  <strong>Final Amount:</strong> $
                                  {bill.finalAmount?.toFixed(2)}
                                </p>
                                <p>
                                  <strong>Pending Amount:</strong> $
                                  {bill.pendingAmount?.toFixed(2)}
                                </p>
                                {bill.campus && (
                                  <>
                                    <p>
                                      <strong>Contact Email:</strong> {bill.campus.contactEmail || "N/A"}
                                    </p>
                                    <p>
                                      <strong>Contact Number:</strong> {bill.campus.contactNumber || "N/A"}
                                    </p>
                                  </>
                                )}
                                <div className="mt-2">
                                  <button
                                    onClick={() => handleGenerateInvoice(bill)}
                                    className="flex items-center bg-[#241763] text-white px-3 py-1 rounded text-sm"
                                  >
                                    <FileText className="w-4 h-4 mr-1" />{" "}
                                    Generate Invoice
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="14" className="text-center p-4">
                    {loading ? "Loading..." : "No Fee Bills Found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination Controls */}
      {!loading && filteredFeeBills.length > 0 && (
        <div className="flex justify-between items-center mt-6">
          <div className="text-sm text-gray-600">
            Showing {startIndex + 1} to {Math.min(startIndex + pageSize, filteredFeeBills.length)} of {filteredFeeBills.length} entries
          </div>
          
          <div className="flex items-center">
            <button
              onClick={() => handlePageChange(pageNumber - 1)}
              className="px-3 py-1 bg-[#241763] text-white rounded mr-2"
              disabled={pageNumber === 1}
            >
              Previous
            </button>
            
            <div className="flex">
              {Array.from(
                { length: Math.min(5, totalPages) },
                (_, i) => {
                  let pageToRender;
                  if (totalPages <= 5) {
                    pageToRender = i + 1;
                  } else if (pageNumber <= 3) {
                    pageToRender = i + 1;
                  } else if (pageNumber >= totalPages - 2) {
                    pageToRender = totalPages - 4 + i;
                  } else {
                    pageToRender = pageNumber - 2 + i;
                  }
                  
                  return (
                    <button
                      key={i}
                      onClick={() => handlePageChange(pageToRender)}
                      className={`px-3 py-1 mx-1 border rounded ${
                        pageNumber === pageToRender
                          ? "bg-[#241763] text-white"
                          : "bg-gray-200"
                      }`}
                    >
                      {pageToRender}
                    </button>
                  );
                }
              )}
              
              {totalPages > 5 && pageNumber < totalPages - 2 && (
                <>
                  <span className="mx-1 self-center">...</span>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    className={`px-3 py-1 mx-1 border rounded ${
                      pageNumber === totalPages
                        ? "bg-[#241763] text-white"
                        : "bg-gray-200"
                    }`}
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>
            
            <button
              onClick={() => handlePageChange(pageNumber + 1)}
              className="px-3 py-1 bg-[#241763] text-white rounded ml-2"
              disabled={pageNumber === totalPages}
            >
              Next
            </button>
          </div>
          
          <div className="flex items-center">
            <span className="mr-2 text-sm">Show:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPageNumber(1); // Reset to first page when changing page size
              }}
              className="border rounded p-1"
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {isModalOpen && (
        <FeeBillsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          data={editingData}
        />
      )}

      {isPaymentModalOpen && (
        <PaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          data={paymentData}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {isNotificationsModal && (
        <NotificationsModal
          selectedItems={selectedStudents}
          toggleItemSelection={toggleStudentSelection}
          handleSubmitNotification={handleSubmitNotification}
          onClose={() => setIsNotificationsModal(false)}
        />
      )}

      <Toaster />
    </div>
  );
}

// Payment Modal Component
function PaymentModal({ isOpen, onClose, data, onSuccess }) {
  const dispatch = useDispatch();
  const { paymentLoading } = useSelector((state) => state.feeStructures);

  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [amount, setAmount] = useState(data?.pendingAmount || 0);
  const [transactionId, setTransactionId] = useState("");
  const [notes, setNotes] = useState("");
  const [receiptAttachment, setReceiptAttachment] = useState(null);
  const [receiptFileName, setReceiptFileName] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReceiptAttachment(file);
      setReceiptFileName(file.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const paymentData = {
      studentId: data.studentId,
      amount: amount,
      receiptAttachment: receiptAttachment,
      paymentMethod: paymentMethod,
      transactionId: transactionId,
      notes: notes,
    };

    try {
      await dispatch(makePayment(paymentData)).unwrap();
      onSuccess();
    } catch (error) {
      console.error("Payment failed:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">Receive Payment</h2>

        <div className="mb-4">
          <p>
            <strong>Student:</strong> {data.studentName}
          </p>
          <p>
            <strong>Enrollment No:</strong> {data.enrollmentNo}
          </p>
          {data.campus && (
            <p>
              <strong>Campus:</strong> {data.campus.name}
            </p>
          )}
          <p>
            <strong>Bill ID:</strong> {data.billId}
          </p>
          <p>
            <strong>Pending Amount:</strong> ${data.pendingAmount?.toFixed(2)}
          </p>
          <p>
            <strong>Due Date:</strong>{" "}
            {data.dueDate ? new Date(data.dueDate).toLocaleDateString() : "N/A"}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="block mb-1">Payment Method</label>
            <select
              className="w-full p-2 border rounded"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <option value="cash">Cash</option>
              <option value="creditCard">Credit Card</option>
              <option value="bankTransfer">Bank Transfer</option>
              <option value="check">Check</option>
            </select>
          </div>

          <div className="mb-3">
            <label className="block mb-1">Amount</label>
            <input
              type="number"
              step="0.01"
              className="w-full p-2 border rounded"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              required
            />
          </div>

          <div className="mb-3">
            <label className="block mb-1">Transaction ID (Optional)</label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
            />
          </div>

          <div className="mb-3">
            <label className="block mb-1">Receipt Attachment</label>
            <div className="flex items-center">
              <input
                type="file"
                id="receiptAttachment"
                className="hidden"
                onChange={handleFileChange}
                accept="image/*,.pdf"
              />
              <label
                htmlFor="receiptAttachment"
                className="cursor-pointer bg-gray-100 px-4 py-2 rounded border border-gray-300 hover:bg-gray-200"
              >
                {receiptFileName || "Choose File"}
              </label>
              {receiptFileName && (
                <button
                  type="button"
                  className="ml-2 text-red-500"
                  onClick={() => {
                    setReceiptAttachment(null);
                    setReceiptFileName("");
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <div className="mb-4">
            <label className="block mb-1">Notes (Optional)</label>
            <textarea
              className="w-full p-2 border rounded"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="3"
            ></textarea>
          </div>

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-100"
              disabled={paymentLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#241763] text-white rounded hover:bg-[#241763]/80"
              disabled={paymentLoading}
            >
              {paymentLoading ? "Processing..." : "Process Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FeeBills;